import { useState, useEffect, useCallback, useRef } from 'react'
import { ChevronLeft, ChevronRight, Upload, X, Loader2, Clock, FileUp } from 'lucide-react'
import api from '../../core/services/api'

interface Props {
  commessaId: string
  onToast: (msg: string, type: 'success' | 'error') => void
}

interface Personale {
  id: string
  nome: string
  cognome: string
}

interface GiornoCalendario {
  data: string
  ore_totali: number
  tipo: string
  codice_busta: string
}

interface ImportResult {
  totale_pagine: number
  buste_importate: number
  errori: number
  dettaglio: { pagina: number; esito: string; errore?: string }[]
}

const TIPO_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  ordinario: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Ordinario' },
  festivo: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Festivo' },
  ferie: { bg: 'bg-green-100', text: 'text-green-700', label: 'Ferie' },
  permesso: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Permesso' },
  malattia: { bg: 'bg-red-100', text: 'text-red-700', label: 'Malattia' },
  riposo: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Riposo' },
}

const GIORNI_SETTIMANA = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

function getFirstDayOfWeek(year: number, month: number): number {
  // 0=Mon ... 6=Sun (ISO)
  const d = new Date(year, month - 1, 1).getDay()
  return d === 0 ? 6 : d - 1
}

export default function OreCalendarioTab({ commessaId, onToast }: Props) {
  const now = new Date()
  const [personaleList, setPersonaleList] = useState<Personale[]>([])
  const [selectedPersonale, setSelectedPersonale] = useState('')
  const [mese, setMese] = useState(now.getMonth() + 1)
  const [anno, setAnno] = useState(now.getFullYear())
  const [calendario, setCalendario] = useState<GiornoCalendario[]>([])
  const [loading, setLoading] = useState(false)

  // Upload modal
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadPersonale()
  }, [])

  const loadPersonale = async () => {
    try {
      const res = await api.get('/v1/personale')
      setPersonaleList(res.data)
    } catch {
      onToast('Errore nel caricamento personale', 'error')
    }
  }

  const loadCalendario = useCallback(async () => {
    if (!selectedPersonale) return
    try {
      setLoading(true)
      const res = await api.get(`/v1/ore/calendario/${selectedPersonale}`, {
        params: { commessa_id: commessaId, mese, anno }
      })
      setCalendario(res.data)
    } catch {
      onToast('Errore nel caricamento calendario', 'error')
    } finally {
      setLoading(false)
    }
  }, [selectedPersonale, commessaId, mese, anno, onToast])

  useEffect(() => {
    loadCalendario()
  }, [loadCalendario])

  const prevMonth = () => {
    if (mese === 1) { setMese(12); setAnno(a => a - 1) }
    else setMese(m => m - 1)
  }

  const nextMonth = () => {
    if (mese === 12) { setMese(1); setAnno(a => a + 1) }
    else setMese(m => m + 1)
  }

  const meseLabel = new Date(anno, mese - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })

  const handleFileUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      onToast('Seleziona un file PDF', 'error')
      return
    }
    setUploading(true)
    setImportResult(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post(`/v1/ore/import-buste-paga?commessa_id=${commessaId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setImportResult(res.data)
      onToast(`Importate ${res.data.buste_importate} buste paga`, 'success')
      loadCalendario()
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore nell\'importazione', 'error')
    } finally {
      setUploading(false)
    }
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileUpload(file)
  }

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileUpload(file)
  }

  // Build calendar grid
  const daysInMonth = getDaysInMonth(anno, mese)
  const firstDay = getFirstDayOfWeek(anno, mese)
  const calendarMap = new Map(calendario.map(g => [g.data, g]))

  // Summary
  const oreOrdinarie = calendario.filter(g => g.tipo === 'ordinario').reduce((s, g) => s + g.ore_totali, 0)
  const oreTotali = calendario.reduce((s, g) => s + g.ore_totali, 0)
  const oreStraordinarie = Math.max(0, oreTotali - oreOrdinarie)

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Dipendente</label>
          <select
            value={selectedPersonale}
            onChange={e => setSelectedPersonale(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Seleziona dipendente...</option>
            {personaleList.map(p => (
              <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <span className="text-lg font-semibold text-gray-800 min-w-[180px] text-center capitalize">
            {meseLabel}
          </span>
          <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <button
          onClick={() => { setShowUpload(true); setImportResult(null) }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Importa Buste Paga
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : !selectedPersonale ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Clock className="w-10 h-10 mb-2" />
            <p>Seleziona un dipendente per visualizzare il calendario</p>
          </div>
        ) : (
          <>
            {/* Weekday headers */}
            <div className="grid grid-cols-7 border-b border-gray-200">
              {GIORNI_SETTIMANA.map(g => (
                <div key={g} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase">
                  {g}
                </div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="h-24 border-b border-r border-gray-100 bg-gray-50" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1
                const dateStr = `${anno}-${String(mese).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                const info = calendarMap.get(dateStr)
                const tipo = info?.tipo || ''
                const colors = TIPO_COLORS[tipo] || { bg: 'bg-gray-50', text: 'text-gray-400', label: '' }
                const isWeekend = ((firstDay + i) % 7) >= 5

                return (
                  <div
                    key={day}
                    className={`h-24 border-b border-r border-gray-100 p-2 flex flex-col ${isWeekend ? 'bg-gray-50/50' : ''}`}
                  >
                    <span className={`text-xs font-medium ${isWeekend ? 'text-gray-400' : 'text-gray-600'}`}>
                      {day}
                    </span>
                    {info && (
                      <div className="flex-1 flex flex-col justify-center items-center gap-1">
                        <span className="text-lg font-bold text-gray-800">{info.ore_totali}h</span>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                          {colors.label || tipo}
                        </span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Summary bar */}
      {selectedPersonale && calendario.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-8">
            <div>
              <span className="text-xs text-gray-500">Ore Ordinarie</span>
              <p className="text-xl font-bold text-blue-600">{oreOrdinarie}h</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Ore Straordinarie</span>
              <p className="text-xl font-bold text-amber-600">{oreStraordinarie}h</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Totale Mese</span>
              <p className="text-xl font-bold text-gray-900">{oreTotali}h</p>
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              {Object.entries(TIPO_COLORS).map(([key, val]) => (
                <span key={key} className={`text-[10px] font-medium px-2 py-1 rounded-full ${val.bg} ${val.text}`}>
                  {val.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Importa Buste Paga</h2>
              <button onClick={() => setShowUpload(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                    <span className="text-sm text-gray-600">Importazione in corso...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <FileUp className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Trascina qui il file PDF oppure <span className="text-primary-600 font-medium">sfoglia</span>
                    </span>
                    <span className="text-xs text-gray-400">Solo file PDF</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={onFileSelect}
                />
              </div>

              {/* Import results */}
              {importResult && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Risultato Importazione</h3>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Pagine</p>
                      <p className="text-lg font-bold text-gray-800">{importResult.totale_pagine}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-green-600">Importate</p>
                      <p className="text-lg font-bold text-green-700">{importResult.buste_importate}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-600">Errori</p>
                      <p className="text-lg font-bold text-red-700">{importResult.errori}</p>
                    </div>
                  </div>
                  {importResult.dettaglio.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Pagina</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Esito</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Errore</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {importResult.dettaglio.map((d, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 text-gray-700">{d.pagina}</td>
                              <td className="px-3 py-1.5">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  d.errore ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                }`}>
                                  {d.esito}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-red-500">{d.errore || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
